import React, { useState, useEffect, useMemo } from 'react';
import { getRecentActivity, getActivityByDateRange } from '../../api/activity';
import PaginationControls from '../../components/common/PaginationControls';
import AdminSidebar from '../../components/admin/AdminSidebar';
import NoticeToast from '../../components/common/NoticeToast';
import { useNotice } from '../../hooks/useNotice';
import './ActivityLogs.css';

const ITEMS_PER_PAGE = 5;

const ActivityLogs = () => {
  const { notice, showNotice, clearNotice } = useNotice();
  const [loading, setLoading] = useState(true);
  const [activity, setActivity] = useState([]);
  const [filterType, setFilterType] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    loadActivity();
  }, []);

  const loadActivity = async () => {
    setLoading(true);
    try {
      const data = await getRecentActivity(100);
      setActivity(data.activity || []);
    } catch (error) {
      console.error('Erreur lors du chargement de l\'activité:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDateFilter = async () => {
    if (!startDate || !endDate) {
      showNotice('Veuillez selectionner une date de debut et de fin.', 'warning');
      return;
    }

    setLoading(true);
    try {
      const data = await getActivityByDateRange(startDate, endDate);
      setActivity(data.activity || []);
    } catch (error) {
      console.error('Erreur lors du filtrage:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetFilters = () => {
    setFilterType('');
    setFilterRole('');
    setSearchTerm('');
    setStartDate('');
    setEndDate('');
    loadActivity();
  };

  const filteredActivity = useMemo(() => {
    const filtered = activity.filter(item => {
    const matchType = !filterType || item.type === filterType;
    const matchRole = !filterRole || item.role === filterRole;
    const matchSearch = !searchTerm || 
      item.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.auteur_nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.cours?.toLowerCase().includes(searchTerm.toLowerCase());
    
      return matchType && matchRole && matchSearch;
    });

    return filtered.sort((a, b) => {
      const dateA = new Date(a.date || 0).getTime();
      const dateB = new Date(b.date || 0).getTime();
      if (dateB !== dateA) {
        return dateB - dateA;
      }
      return (b.id || 0) - (a.id || 0);
    });
  }, [activity, filterType, filterRole, searchTerm]);

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(filteredActivity.length / ITEMS_PER_PAGE));
  }, [filteredActivity.length]);

  const activityKpis = useMemo(() => {
    const validationCount = activity.filter((item) => item.type === 'validation').length;
    const uploadCount = activity.filter((item) => item.type === 'plan_upload').length;
    const statusChangeCount = activity.filter((item) => item.type === 'status_change').length;

    return [
      { label: 'Total activités', value: activity.length, helper: 'historique chargé' },
      { label: 'Validations', value: validationCount, helper: 'actions de validation' },
      { label: 'Soumissions', value: uploadCount, helper: 'dépôts de plans' },
      { label: 'Changements statut', value: statusChangeCount, helper: 'transitions suivies' },
      { label: 'Résultats filtrés', value: filteredActivity.length, helper: 'vue actuelle' },
    ];
  }, [activity, filteredActivity.length]);

  const paginatedActivity = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredActivity.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredActivity, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filterType, filterRole, searchTerm, startDate, endDate]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const getTypeIcon = (type) => {
    switch (type) {
      case 'validation':
        return 'fa-check-circle';
      case 'plan_upload':
        return 'fa-upload';
      case 'status_change':
        return 'fa-sync-alt';
      default:
        return 'fa-circle';
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'validation':
        return '#27ae60';
      case 'plan_upload':
        return '#3498db';
      case 'status_change':
        return '#f39c12';
      default:
        return '#95a5a6';
    }
  };

  const getRoleColor = (role) => {
    const colors = {
      'EN': '#3498db',
      'CE': '#9b59b6',
      'CP': '#e67e22',
      'DA': '#27ae60',
      'SC': '#e74c3c'
    };
    return colors[role] || '#95a5a6';
  };

  if (loading) {
    return (
      <div className="admin-shell">
        <AdminSidebar />
        <div className="admin-content">
          <div className="activity-logs">
            <div className="loading-spinner">Chargement de l'activité...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-shell">
      <AdminSidebar />
      <div className="admin-content">
        <div className="activity-logs">
      {/* Header */}
      <div className="activity-header">
        <div>
          <h1>Journal d'activité</h1>
          <p className="activity-count">{filteredActivity.length} activité(s) trouvée(s)</p>
        </div>
      </div>

      <div className="activity-kpi-strip">
        {activityKpis.map((kpi) => (
          <div key={kpi.label} className="activity-kpi-card">
            <span className="activity-kpi-label">{kpi.label}</span>
            <strong className="activity-kpi-value">{kpi.value}</strong>
            <span className="activity-kpi-helper">{kpi.helper}</span>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="search-box">
          <i className="fas fa-search"></i>
          <input
            type="text"
            placeholder="Rechercher dans l'activité..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <select 
          value={filterType} 
          onChange={(e) => setFilterType(e.target.value)}
          className="filter-select"
        >
          <option value="">Tous les types</option>
          <option value="validation">Validations</option>
          <option value="plan_upload">Soumissions</option>
          <option value="status_change">Changements de statut</option>
        </select>

        <select 
          value={filterRole} 
          onChange={(e) => setFilterRole(e.target.value)}
          className="filter-select"
        >
          <option value="">Tous les rôles</option>
          <option value="EN">Enseignant</option>
          <option value="CE">Chef d'équipe</option>
          <option value="CP">Conseiller pédagogique</option>
          <option value="DA">Direction adjointe</option>
          <option value="SC">Spécialiste du contenu</option>
        </select>

        <div className="date-filters">
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="date-input"
          />
          <span>à</span>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="date-input"
          />
          <button 
            className="btn-filter"
            onClick={handleDateFilter}
          >
            <i className="fas fa-filter"></i> Filtrer
          </button>
        </div>

        <button 
          className="btn-reset"
          onClick={resetFilters}
        >
          <i className="fas fa-times"></i> Réinitialiser
        </button>
      </div>

      {/* Activity Timeline */}
      <div className="activity-timeline-container">
        {filteredActivity.length > 0 ? (
          <>
            <div className="activity-timeline">
              {paginatedActivity.map((item, index) => (
                <div key={item.id || index} className="timeline-item">
                  <div 
                    className="timeline-icon"
                    style={{ backgroundColor: getTypeColor(item.type) }}
                  >
                    <i className={`fas ${getTypeIcon(item.type)}`}></i>
                  </div>
                  <div className="timeline-content">
                    <div className="timeline-header">
                      <div className="timeline-title">
                        <span className="timeline-description">{item.description}</span>
                        {item.role && (
                          <span 
                            className="role-badge"
                            style={{ backgroundColor: getRoleColor(item.role) }}
                          >
                            {item.role}
                          </span>
                        )}
                      </div>
                      <span className="timeline-date">
                        {item.date ? new Date(item.date).toLocaleString('fr-FR', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        }) : 'Date inconnue'}
                      </span>
                    </div>
                    {item.auteur_nom && (
                      <div className="timeline-meta">
                        <i className="fas fa-user"></i>
                        <span>Par: <strong>{item.auteur_nom}</strong></span>
                      </div>
                    )}
                    {item.cours && (
                      <div className="timeline-meta">
                        <i className="fas fa-book"></i>
                        <span>Cours: <strong>{item.cours}</strong></span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          
            <PaginationControls
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </>
        ) : (
          <div className="no-activity">
            <i className="fas fa-inbox"></i>
            <p>Aucune activité trouvée</p>
          </div>
        )}
      </div>
      <NoticeToast notice={notice} onClose={clearNotice} />
        </div>
      </div>
    </div>
  );
};

export default ActivityLogs;
